import { useState } from 'react';
import { Globe, Lock } from 'lucide-react';
import { TabsContent } from '@/components/ui/tabs';
import TabbedModal, { type TabbedModalTab } from '../tabbed-modal/TabbedModal';
import InternalShareTab from './InternalShareTab';
import PhotosetGalleryTab from './PhotosetGalleryTab';
import type { GalleryLinkGate } from './shareGate';
import type { PublicLink } from './usePublicLink';
import { useInternalShare } from './useInternalShare';

type ShareTab = 'internal' | 'gallery';

const TABS: readonly TabbedModalTab[] = [
  { value: 'internal', label: 'Internal Share', icon: Lock },
  { value: 'gallery', label: 'PlanOtour', icon: Globe }
];

const isShareTab = (value: string): value is ShareTab => value === 'internal' || value === 'gallery';

interface ShareModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  photosetId: string;
  /** The property's account, named by Internal Share. */
  accountName: string | null;
  link: PublicLink;
  galleryLink: GalleryLinkGate;
  /** See TabbedModal. */
  onCloseAutoFocus?: (event: Event) => void;
}

/**
 * The header's Share modal (DV-3714): Internal Share, for the account's team,
 * and PlanOtour, the public link. Opens on Internal Share.
 */
const ShareModal = ({
  open,
  onOpenChange,
  photosetId,
  accountName,
  link,
  galleryLink,
  onCloseAutoFocus
}: ShareModalProps) => {
  const [tab, setTab] = useState<ShareTab>('internal');
  const close = () => onOpenChange(false);
  // Held here rather than in the tab, which unmounts while the other tab
  // shows: the checked members and the note survive a look at the gallery.
  // A send that reaches everyone closes the modal.
  const internalShare = useInternalShare({
    photosetId,
    hiddenFromClient: galleryLink.hiddenFromClient,
    onSent: close
  });
  // A send in flight holds the modal open: closing it would unmount the send
  // and lose its outcome, and a modal reopened before it settled would offer
  // Send again. TabbedModal holds its own close paths; Cancel and Done are ours.
  const dismissible = !internalShare.sending;
  const dismiss = () => {
    if (dismissible) close();
  };

  return (
    <TabbedModal
      open={open}
      onOpenChange={onOpenChange}
      dismissible={dismissible}
      title="Share"
      tabs={TABS}
      value={tab}
      onValueChange={(value) => {
        if (isShareTab(value)) setTab(value);
      }}
      sheetTestId="share-sheet"
      onCloseAutoFocus={onCloseAutoFocus}
    >
      <TabsContent value="internal" className="mt-0 outline-none">
        <InternalShareTab
          share={internalShare}
          accountName={accountName}
          onUseGallery={() => setTab('gallery')}
          onClose={dismiss}
        />
      </TabsContent>
      <TabsContent value="gallery" className="mt-0 outline-none">
        <PhotosetGalleryTab
          photosetId={photosetId}
          link={link}
          gate={galleryLink}
          onDone={dismiss}
        />
      </TabsContent>
    </TabbedModal>
  );
};

export default ShareModal;
